import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import MyCases from "@/pages/MyCases";
import MyDay from "@/pages/MyDay";
import Standup from "@/pages/Standup";
import ConsultationBoard from "@/pages/ConsultationBoard";
import PresalesSettingsPage from "@/pages/PresalesSettingsPage";
import Weekly from "@/pages/Weekly";
import Wire from "@/pages/Wire";
import CaseWorkspace from "@/pages/CaseWorkspace";
import AdminConsole from "@/pages/AdminConsole";
import DesignGallery from "@/pages/DesignGallery";
import PartnerCases from "@/pages/PartnerCases";
import PartnerCaseView from "@/pages/PartnerCaseView";
import PreviewPartner from "@/pages/PreviewPartner";
import Leaderboard from "@/pages/Leaderboard";
import Presales from "@/pages/Presales";
import PresalesAnalytics from "@/pages/PresalesAnalytics";
import PresalesScript from "@/pages/PresalesScript";
import PresalesMyCalls from "@/pages/PresalesMyCalls";
import PresalesTeam from "@/pages/PresalesTeam";
import { RaceGate } from "@/components/RaceGate";
import { Loader2 } from "lucide-react";

function roleHome(user, activeRole) {
  const r = user?.roles || [];
  const staff = r.some((x) => ["RM", "Manager", "Admin"].includes(x));
  if (r.includes("Partner") && !staff) return "/partner";
  // Anyone with the RM role lands on My Day (RM-only, RM+Manager, Admin+Manager+RM); Manager/Admin-only → Standup.
  if (r.includes("RM") || activeRole === "RM") return "/my-day";
  return "/standup";
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return <RaceGate>{children}</RaceGate>;
}

function LoginGate() {
  const { user, loading, activeRole } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to={roleHome(user, activeRole)} replace />;
  return <Login />;
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback synchronously (fragment), before any auth check runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<LoginGate />} />
      <Route path="/cases" element={<ProtectedRoute><MyCases /></ProtectedRoute>} />
      <Route path="/my-day" element={<ProtectedRoute><MyDay /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/presales" element={<ProtectedRoute><Presales /></ProtectedRoute>} />
      <Route path="/presales/analytics" element={<ProtectedRoute><PresalesAnalytics /></ProtectedRoute>} />
      <Route path="/presales/script" element={<ProtectedRoute><PresalesScript /></ProtectedRoute>} />
      <Route path="/presales/my-calls" element={<ProtectedRoute><PresalesMyCalls /></ProtectedRoute>} />
      <Route path="/presales/team" element={<ProtectedRoute><PresalesTeam /></ProtectedRoute>} />
      <Route path="/presales/settings" element={<ProtectedRoute><PresalesSettingsPage /></ProtectedRoute>} />
      <Route path="/consultations" element={<ProtectedRoute><ConsultationBoard /></ProtectedRoute>} />
      <Route path="/standup" element={<ProtectedRoute><Standup /></ProtectedRoute>} />
      <Route path="/weekly" element={<ProtectedRoute><Weekly /></ProtectedRoute>} />
      <Route path="/wire" element={<ProtectedRoute><Wire /></ProtectedRoute>} />
      <Route path="/cases/:recordId" element={<ProtectedRoute><CaseWorkspace /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminConsole /></ProtectedRoute>} />
      <Route path="/admin/design" element={<ProtectedRoute><DesignGallery /></ProtectedRoute>} />
      <Route path="/admin/preview-partner" element={<ProtectedRoute><PreviewPartner /></ProtectedRoute>} />
      <Route path="/partner" element={<ProtectedRoute><PartnerCases /></ProtectedRoute>} />
      <Route path="/partner/cases/:recordId" element={<ProtectedRoute><PartnerCaseView /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
